import React, { useState, useEffect } from 'react';
import { CheckCircle2, Award, Target, Users, ChevronLeft, ChevronRight } from 'lucide-react';
import { companyInfo } from '../mock';
import { backgroundImages } from '../realPhotosData';
import { UrgentContactModal } from './UrgentContactModal';

const AboutCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Cache-busting: ajoute un timestamp pour forcer le rechargement
  const cacheBuster = Date.now();
  const images = [
    `/images/contact1.jpg?v=${cacheBuster}`,
    `/images/contact2.jpg?v=${cacheBuster}`,
    `/images/contact3.jpg?v=${cacheBuster}`,
    `/images/contact4.jpg?v=${cacheBuster}`,
    `/images/contact5.jpg?v=${cacheBuster}`,
    `/images/about_paris_7.jpg?v=${cacheBuster}`,
    `/images/about_paris_8.jpg?v=${cacheBuster}`,
    `/images/about_paris_9.jpg?v=${cacheBuster}`,
    `/images/about_paris_11.jpg?v=${cacheBuster}`,
    `/images/about_paris_13.jpg?v=${cacheBuster}`,
    `/images/about_paris_15.jpg?v=${cacheBuster}`,
    `/images/about_paris_16.jpg?v=${cacheBuster}`,
    `/images/about_paris_17.jpg?v=${cacheBuster}`
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? images.length - 1 : currentIndex - 1);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === images.length - 1 ? 0 : currentIndex + 1);
  };

  return (
    <div className="relative h-full overflow-hidden rounded-2xl shadow-2xl">
      {images.map((img, index) => (
        <div
          key={`about-img-${img}`}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentIndex ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={img}
            alt={`Vue de Paris ${index + 1}`}
            className="w-full h-full object-cover"
          />
        </div>
      ))}
      <button
        onClick={goToPrevious}
        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black p-2 rounded-full shadow-lg z-10 transition-all"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-black p-2 rounded-full shadow-lg z-10 transition-all"
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {images.map((img, index) => (
          <button
            key={`about-dot-${img}`}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentIndex ? 'bg-orange-600 w-6' : 'bg-white/60'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export const AboutSection = () => {
  const [urgentOpen, setUrgentOpen] = useState(false);

  const highlights = [
    {
      icon: Award,
      title: 'Expertise reconnue',
      description: '13+ ans d\'expérience en travaux acrobatiques',
      action: null
    },
    {
      icon: Target,
      title: 'Précision & Rapidité',
      description: 'Interventions sous 24-48h en cas d\'urgence',
      action: () => setUrgentOpen(true)
    },
    {
      icon: Users,
      title: 'Satisfaction client',
      description: 'À taille humaine, à votre écoute',
      action: null
    }
  ];

  return (
    <section id="apropos" className="bg-white py-20">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-4xl mx-auto mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-4">
            À propos d'<span className="text-orange-600">AcrobaTisseur</span>
          </h2>
          <p className="text-lg text-gray-500">Découvrez notre univers et nos chantiers en hauteur</p>
        </div>

        {/* Content Grid: Text (left) + Carousel (right) */}
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left - Description */}
          <div>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {companyInfo.description}
            </p>
            <p className="text-lg text-gray-700 mb-8 leading-relaxed">
              {companyInfo.mission}
            </p>

            {/* Values */}
            <div className="space-y-3 mb-2">
              {companyInfo.values.map((value) => (
                <div key={`value-${value}`} className="flex items-center gap-3">
                  <CheckCircle2 className="text-orange-600 flex-shrink-0" size={24} />
                  <span className="text-gray-700 font-medium">{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Right - Carousel + Highlights */}
          <div className="space-y-6 lg:sticky lg:top-24">
            <div className="relative w-full aspect-[16/9]">
              <AboutCarousel />
            </div>

            {/* Highlights Cards */}
            <div className="space-y-3">
              {highlights.map((item, index) => {
                const Icon = item.icon;
                const isClickable = !!item.action;
                const baseClasses = "flex items-start gap-4 p-4 bg-gray-50 rounded-lg transition-colors";
                const clickableClasses = isClickable
                  ? "cursor-pointer hover:bg-red-50 hover:ring-2 hover:ring-red-500/40 group"
                  : "hover:bg-orange-50";
                return (
                  <div
                    key={`highlight-${item.title}`}
                    onClick={item.action || undefined}
                    role={isClickable ? "button" : undefined}
                    tabIndex={isClickable ? 0 : undefined}
                    onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') item.action(); } : undefined}
                    data-testid={isClickable ? 'urgent-trigger-card' : `highlight-card-${index}`}
                    className={`${baseClasses} ${clickableClasses}`}
                  >
                    <div className={`${isClickable ? 'bg-red-600 group-hover:scale-110' : 'bg-orange-600'} text-white p-3 rounded-lg transition-transform`}>
                      <Icon size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-black mb-1 flex items-center gap-2">
                        {item.title}
                        {isClickable && (
                          <span className="text-xs font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full uppercase tracking-wide">
                            Urgence
                          </span>
                        )}
                      </h3>
                      <p className="text-gray-600 text-sm">{item.description}</p>
                      {isClickable && (
                        <p className="text-red-600 text-xs font-semibold mt-1 group-hover:underline">
                          → Demander une intervention urgente
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Centered Slogan / Quote */}
        <div className="mt-16 max-w-3xl mx-auto text-center">
          <p className="text-2xl md:text-3xl font-semibold italic text-gray-800 px-4">
            <span className="text-orange-600 text-4xl leading-none align-top mr-1">“</span>
            {companyInfo.difference}
            <span className="text-orange-600 text-4xl leading-none align-top ml-1">”</span>
          </p>
        </div>

        {/* Location Banner */}
        <div className="mt-16 bg-black text-white p-8 rounded-2xl text-center">
          <p className="text-xl font-semibold">{companyInfo.location}</p>
        </div>
      </div>

      <UrgentContactModal isOpen={urgentOpen} onClose={() => setUrgentOpen(false)} />
    </section>
  );
};
